import React, {ReactNode, FC} from 'react';

import {RoundButton} from '@optimaxdev/design-system';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {Price} from 'components/price/price';

import {ApplicationStoreType} from 'desktop/store';

import {getCartItemById} from '../../store/selectors';
import {CartItemEyeglassesType, CartItemType} from '../../types';

import {Benefits, getFeatures} from './getFeatures';
import {getCoPayValue} from './helpers';

import s from './insurancePopup.scss';

type InsurancePopupButtonType = {
    label: ReactNode;
    onClick: (buttonLocation: 'Top' | 'Bottom') => void;
    key: string;
    isAdded?: boolean;
};

type PropsType = {
    closePopup: () => void;
    buttons: InsurancePopupButtonType[];
    itemId?: string;
    cartItem?: CartItemType | null;
};

const locationClassNames = {
    Bottom: s.bottom,
    Top: s.top,
};

/**
 * Provides the data for coverage popup
 * @param {PropsType} - props
 * @returns {React.ReactNode} - component
 */
export const InsurancePopup: FC<PropsType> = ({buttons = [], cartItem, closePopup}) => {
    const getButtonsBlock = (buttonLocation: 'Top' | 'Bottom') => (
        <>
            {buttons.map((button: InsurancePopupButtonType, index: number) => {
                return (
                    <div
                        className={classNames(s.buttonWrapper, locationClassNames[buttonLocation])}
                        key={button.key}
                    >
                        <RoundButton
                            onClick={() => {
                                button.onClick(buttonLocation);
                            }}
                            size="medium"
                            variant={index % 2 === 0 && buttons.length > 1 ? 'ghost' : 'painted'}
                        >
                            {button.label}
                        </RoundButton>
                    </div>
                );
            })}
            {buttons.length === 1 && buttonLocation === 'Top' && (
                <button className={s.back} onClick={closePopup} type="button">
                    Back
                </button>
            )}
        </>
    );

    return (
        <>
            <div className={s.mainContainer}>
                <div className={s.img}>
                    <Benefits />
                </div>
                <div className={s.infoBlock}>
                    <h3 className={s.title}>Your protection plan.</h3>
                    <h4 className={s.subTitle}>20/20 Enhanced Coverage</h4>
                    <div className={s.divider} />
                    <p className={s.description}>
                        Enjoy your new glasses worry-free with our longer comprehensive protection
                        plan.
                    </p>
                    <div className={classNames(s.cta, {[s.singleCta]: buttons.length === 1})}>
                        {getButtonsBlock('Top')}
                    </div>
                </div>
            </div>
            <div className={s.container}>
                <div className={s.titleExplonation}>How Does It Work?</div>
                <div className={s.featureContainer}>
                    {getFeatures(cartItem as CartItemEyeglassesType).map(item => (
                        <div className={s.featureItem} key={item.title}>
                            <div className={classNames(s.featureIcon, item.className)}>
                                {item.icon}
                            </div>
                            <h5 className={s.featureTitle}>{item.title}</h5>
                            <p className={classNames(s.featureDescription, item.descriptionClass)}>
                                {item.description}
                            </p>
                        </div>
                    ))}
                </div>
                <div className={s.ctaBottom}>{getButtonsBlock('Bottom')}</div>
                <p className={s.notification}>
                    * The customer is entitled to a one time replacement of the same frame (or equal
                    valued frame) with same lens package and coatings as in the original order. If a
                    prescription change requires an upgrade in lens package, the customer will pay
                    the difference. Product must be returned to GlassesUSA prior to exchange. A
                    Co-Pay amount of{' '}
                    <Price>{getCoPayValue(cartItem as CartItemEyeglassesType)}</Price> will be
                    charged by GlassesUSA.com for any exchange within this coverage. For full
                    details see our Terms of Use.
                </p>
            </div>
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @param props
 *
 */
export const mapStateToProps = (store: ApplicationStoreType, props?: {itemId?: string}) => ({
    cartItem: props?.itemId ? getCartItemById(store, props.itemId) : null,
});

export const InsurancePopupConnected = connect(mapStateToProps)(InsurancePopup);
